import {
  Component,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';

/**
 * Generic form field wrapper: label + content projection + validation errors.
 * Colours (label, error, focus ring) driven by CSS tokens from variables.scss.
 *
 * Usage:
 *   <app-form-field label="Party Name" [control]="form.get('partyName')" [required]="true">
 *     <input pInputText formControlName="partyName" />
 *   </app-form-field>
 *
 *   <app-form-field label="State" hint="Select the stamp state" [control]="form.get('state')">
 *     <p-select formControlName="state" [options]="states"></p-select>
 *   </app-form-field>
 */
@Component({
  selector: 'app-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormFieldComponent {
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() hint: string = '';
  @Input() control: AbstractControl | null = null;

  get hasError(): boolean {
    return !!(this.control && this.control.invalid &&
      (this.control.dirty || this.control.touched));
  }

  get errorMessage(): string {
    if (!this.control?.errors) return '';
    const errors = this.control.errors;

    if (errors['required'])   return `${this.label || 'This field'} is required.`;
    if (errors['email'])      return 'Please enter a valid email address.';
    if (errors['minlength']) {
      const req = errors['minlength'].requiredLength;
      return `Minimum ${req} characters required.`;
    }
    if (errors['maxlength']) {
      const req = errors['maxlength'].requiredLength;
      return `Maximum ${req} characters allowed.`;
    }
    if (errors['pattern'])    return 'Invalid format.';
    if (errors['min'])        return `Minimum value is ${errors['min'].min}.`;
    if (errors['max'])        return `Maximum value is ${errors['max'].max}.`;

    const firstKey = Object.keys(errors)[0];
    return errors[firstKey]?.message ?? 'Invalid value.';
  }
}
